import { useEffect, useRef, useState } from 'react';
import { Download, FileText, Printer, Receipt, X } from 'lucide-react';

import { fetchDocument, type DocumentFormat } from '../lib/documents';
import { canRenderPdfInline } from '../lib/platform';
import { cn } from '../lib/cn';

/**
 * Download, print and close.
 *
 * One class for all three, and `qs-icon-button` rather than padding alone: these
 * are a 13px glyph in a box that a touch screen grows to 40px, and without the
 * centring each icon stayed in the corner it was padded into. Download has to be
 * an `<a>` to carry a filename, so the touch sizing cannot come from the
 * `header button` rule — see `.qs-icon-button` in styles/index.css.
 */
const actionClass =
  'qs-icon-button h-7 w-7 rounded border border-rule text-ink-muted transition-colors';

export interface DocumentRequest {
  /** API path without the format parameter. */
  path: string;
  /** Shown in the panel header while loading and after. */
  title: string;
  /** Formats offered for this document. */
  formats?: DocumentFormat[];
}

/**
 * Shows a generated document in a side panel.
 *
 * Text is rendered in a `<pre>` rather than an iframe: a receipt is 48 columns
 * of monospace and putting it in a nested browsing context would cost a
 * scrollbar and gain nothing. PDFs go in an object, which is the only way to get
 * the browser's own viewer.
 */
export function DocumentViewer({
  request,
  onClose,
}: {
  request: DocumentRequest | null;
  onClose: () => void;
}): React.JSX.Element | null {
  const offered = request?.formats ?? ['text', 'pdf'];
  const [format, setFormat] = useState<DocumentFormat>(offered[0] ?? 'text');
  const pressedBackdrop = useRef(false);
  const [state, setState] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; url: string; text: string | null; filename: string; isText: boolean }
    | { status: 'error'; message: string }
  >({ status: 'idle' });

  const path = request?.path;

  /**
   * Each document opens in the first format it is actually offered in.
   *
   * Without this the panel always started on `text`, which was right when every
   * document was generated in both. A purchase invoice exists only as the PDF
   * the till rendered, so the panel would open with neither format button
   * pressed, and switching to the one that was offered would look like a no-op
   * because the server had already sent the PDF regardless.
   */
  useEffect(() => {
    if (!path) return;
    setFormat((current) => (offered.includes(current) ? current : (offered[0] ?? 'text')));
    // `offered` is derived from `request`, and `request` changes identity on
    // every open; keying on the path is what makes this fire once per document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (!path) {
      setState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    let revoke: (() => void) | null = null;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const result = await fetchDocument(path, format);
        // The panel may have been closed, or the format switched again, while
        // this was in flight. Releasing the blob here rather than showing it
        // keeps a superseded document out of the panel and out of memory.
        if (cancelled) {
          result.revoke();
          return;
        }
        revoke = result.revoke;

        // Decided by what came back, not by what was asked for.
        const isText = result.contentType.startsWith('text/');
        const text = isText ? await (await fetch(result.url)).text() : null;
        setState({ status: 'ready', url: result.url, text, filename: result.filename, isText });
      } catch (error) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Could not load the document.',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      revoke?.();
    };
  }, [path, format]);

  useEffect(() => {
    if (!request) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, onClose]);

  if (!request) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-ink/20 backdrop-blur-[1px]"
      // Both ends of the gesture must land on the backdrop. Selecting text in
      // the receipt and releasing past the panel's edge otherwise dispatches the
      // click on this element and closed the panel mid-selection — the same
      // defect the edit dialogs had.
      onMouseDown={(event) => {
        pressedBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pressedBackdrop.current) onClose();
        pressedBackdrop.current = false;
      }}
      role="presentation"
    >
      {/* shadow-float, not Tailwind's shadow-xl: that one is tuned for a white
          ground and all but disappears on the dark theme's near-black. */}
      <aside
        className="flex h-full w-full max-w-2xl flex-col bg-surface shadow-float"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={request.title}
      >
        <header className="flex items-center gap-2 border-b border-rule px-3 py-2">
          <FileText size={15} className="shrink-0 text-ink-faint" aria-hidden />
          {/* Truncates: the title carries a supplier or account name, which is
              free text, and a long one pushed the controls off the edge. */}
          <h2 className="min-w-0 truncate text-xs font-medium" title={request.title}>
            {request.title}
          </h2>

          <span className="ml-auto" />

          {/* Omitted when there is nothing to choose between: a single-format
              document showing one pressed button invites a click that does
              nothing. A purchase invoice is only ever the PDF the till made. */}
          {offered.length > 1 && (
            <div className="flex items-center gap-1" role="group" aria-label="Format">
              {offered.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setFormat(option)}
                  aria-pressed={format === option}
                  className={cn(
                    'rounded border px-2 py-0.5 text-2xs uppercase transition-colors',
                    format === option
                      ? 'border-accent bg-accent-soft font-medium text-accent'
                      : 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink',
                  )}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {state.status === 'ready' && (
            <a
              href={state.url}
              download={state.filename}
              className={cn(actionClass, 'hover:border-accent hover:text-accent')}
              title="Download"
            >
              <Download size={13} aria-hidden />
              <span className="sr-only">Download</span>
            </a>
          )}

          {/* Print is offered for both kinds — it used to be conditional on the
              document being text, which meant the two most likely to be wanted
              on paper, a supplier invoice and a Z reading's PDF, were the two
              that could not be printed.

              Not on iOS for a PDF, though: printing one means loading it into a
              frame and calling `print()` on that frame, and iOS renders neither.
              The button would be there and do nothing, which is worse than not
              offering it — the panel points at the share sheet instead, where
              iOS keeps its own Print. */}
          {state.status === 'ready' && (state.isText || canRenderPdfInline()) && (
            <button
              type="button"
              onClick={() =>
                state.isText
                  ? printText(state.text ?? '', request.title)
                  : printPdf(state.url)
              }
              className={cn(actionClass, 'hover:border-accent hover:text-accent')}
              title="Print"
            >
              <Printer size={13} aria-hidden />
              <span className="sr-only">Print</span>
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className={cn(actionClass, 'hover:border-rule-strong hover:text-ink')}
          >
            <X size={13} aria-hidden />
            <span className="sr-only">Close</span>
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto bg-paper p-3">
          {state.status === 'loading' && (
            <p className="py-8 text-center text-xs text-ink-faint">Generating…</p>
          )}

          {state.status === 'error' && (
            <p role="alert" className="py-8 text-center text-xs text-down">
              {state.message}
            </p>
          )}

          {state.status === 'ready' &&
            (state.isText ? (
              /* Held at 11px rather than taking the phone type scale's extra
                 pixel. This is 48 columns of fixed-width receipt, not prose: the
                 line length is the till's, and at 12px those 48 characters no
                 longer fit a 390px screen — the reader would gain a pixel of
                 type and lose the right-hand column of every total to a sideways
                 swipe. */
              <pre className="qs-panel overflow-x-auto p-3 font-mono text-[0.6875rem] leading-snug whitespace-pre">
                {state.text}
              </pre>
            ) : !canRenderPdfInline() ? (
              <PdfElsewhere url={state.url} filename={state.filename} />
            ) : (
              /*
               * An iframe, not an `<object>`.
               *
               * The page is served with `object-src 'none'` — see nginx.conf,
               * where it is one of the deliberate injection-route closures — and
               * that directive blocks a plugin-rendered PDF outright. Every
               * supplier invoice and every reading PDF therefore fell through to
               * the "your browser cannot display PDFs" fallback on a desktop
               * browser that displays PDFs perfectly well. An iframe is governed
               * by `frame-src`, which this CSP does not restrict, so the fix is
               * the element rather than a hole in the policy.
               *
               * An iframe has no fallback content, so the download line below is
               * always rendered — it is also the answer on iOS, which shows a
               * framed PDF as a single unscrollable page.
               */
              <div className="flex h-full min-h-[60vh] flex-col gap-2">
                <iframe
                  src={state.url}
                  className="min-h-0 w-full flex-1 rounded border border-rule bg-surface"
                  title={request.title}
                />
                <p className="text-2xs text-ink-faint">
                  Not showing, or want it on paper?{' '}
                  <a href={state.url} download={state.filename} className="underline">
                    Download the PDF
                  </a>
                  .
                </p>
              </div>
            ))}
        </div>
      </aside>
    </div>
  );
}

/**
 * A PDF on a device that will not show one in the page.
 *
 * This is iOS, and it is the whole reason the check exists: an embedded PDF
 * there is a blank white rectangle, which reads as a document that failed to
 * load rather than as a platform that cannot draw it. So the panel says what is
 * true and hands over the two routes that do work — opening it, which gives
 * Safari's own viewer and with it the share sheet's Print, and saving it.
 *
 * Open is a link rather than `window.open`, because a standalone PWA on iOS has
 * no popup to open and a target of `_blank` is the gesture that hands a document
 * to the browser.
 */
function PdfElsewhere({ url, filename }: { url: string; filename: string }): React.JSX.Element {
  return (
    <div className="qs-panel flex flex-col items-start gap-3 p-4">
      <div className="flex items-center gap-2">
        <FileText size={15} className="shrink-0 text-ink-faint" aria-hidden />
        <p className="text-xs font-medium">This is a PDF</p>
      </div>

      <p className="text-2xs leading-relaxed text-ink-muted">
        iPhone and iPad cannot show a PDF inside an app. Open it to read it in Safari, where
        Share → Print will send it to a printer, or save it to Files.
      </p>

      <div className="flex flex-wrap gap-2">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="rounded bg-accent px-3 py-2 text-xs font-medium text-ink-inverse hover:bg-accent-hover"
        >
          Open
        </a>
        <a
          href={url}
          download={filename}
          className="rounded border border-rule px-3 py-2 text-xs text-ink-muted hover:border-accent hover:text-accent"
        >
          Save to Files
        </a>
      </div>
    </div>
  );
}

/**
 * The per-row action that opens a receipt.
 *
 * Icon-only because it sits in a table row that already has a lot of numbers in
 * it, with the label carried by `title` and a screen-reader span rather than
 * dropped.
 */
export function ReceiptButton({
  onOpen,
  label,
}: {
  onOpen: () => void;
  label: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      title={label}
      className="rounded border border-rule px-1.5 py-0.5 text-ink-muted hover:border-accent hover:text-accent"
    >
      <Receipt size={12} aria-hidden />
      <span className="sr-only">{label}</span>
    </button>
  );
}

/**
 * An offscreen frame to print from.
 *
 * Printing the app's own page would print the console around the document, and a
 * thermal printer would render the navigation. `right: 100%` rather than
 * `display: none`, because a hidden frame does not lay out and a frame that has
 * not laid out has nothing to print.
 */
function hiddenFrame(): HTMLIFrameElement {
  const frame = window.document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '100%';
  frame.style.width = '0';
  frame.style.height = '0';
  window.document.body.append(frame);
  return frame;
}

/**
 * Prints a PDF.
 *
 * The same hidden frame as the text path, pointed at the blob rather than
 * written into: the browser's own PDF viewer renders it and answers `print()`.
 * Loading is asynchronous here — a blob still has to be parsed — so the dialog
 * is opened from the frame's `load`, not immediately after setting `src`.
 *
 * Safari is the weak spot: it may ignore the call, which is why the panel keeps
 * a download link beside the document rather than treating print as the only
 * route to paper.
 */
function printPdf(url: string): void {
  const frame = hiddenFrame();

  frame.addEventListener('load', () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
  });
  frame.src = url;

  window.setTimeout(() => frame.remove(), 60_000);
}

/** Prints receipt text, as a monospace block and nothing else. */
function printText(text: string, title: string): void {
  const frame = hiddenFrame();

  const doc = frame.contentDocument;
  if (!doc) {
    frame.remove();
    return;
  }

  doc.open();
  doc.write(
    `<!doctype html><title>${escapeHtml(title)}</title>` +
      '<style>body{margin:0}pre{font:11px/1.35 ui-monospace,Menlo,Consolas,monospace}</style>' +
      `<pre>${escapeHtml(text)}</pre>`,
  );
  doc.close();

  frame.contentWindow?.focus();
  frame.contentWindow?.print();

  // The print dialog is modal but not awaitable, so the frame is cleaned up on
  // a timer rather than after it closes.
  window.setTimeout(() => frame.remove(), 60_000);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
